import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Database,
  Globe,
  History,
  Mail,
  Receipt,
  RefreshCcw,
  UserPlus,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Money } from '../../components/Money';
import { Avatar } from '../../components/Avatar';
import { Tabs } from '../../components/Tabs';
import { Banner } from '../../components/Banner';
import { Modal } from '../../components/Modal';
import { Input } from '../../components/Input';
import { PosRoleSelector } from '../../components/PosRoleSelector';
import { InviteLinkResult } from '../../components/InviteLinkResult';
import { CreateOrInviteToggle, AccountCreatedPanel } from '../../components/CreateOrInvite';
import { CashPaymentModal } from '../../components/CashPaymentModal';
import { LedgerView } from '../../components/LedgerView';
import { BusinessUsersTable } from '../../components/BusinessUsersTable';
import { formatDate } from '../../lib/format';
import type { ResellerCustomer } from '../../mocks/reseller';
import {
  useResellerCustomers,
  useCustomerDomains,
  useCustomerAudit,
  createResellerCustomerUser,
  type CustomerDomain,
  type CustomerAuditEvent,
  type ResellerInviteResult,
  type ResellerCreateUserResult,
} from '../../api/reseller';
import { useResellerCustomerLedger, type BusinessLedger } from '../../api/billing';

const FALLBACK_CUSTOMER = {
  id: 'fallback',
  name: 'Customer',
  ownerEmail: 'owner@example.com',
  plan: 'Starter',
  status: 'active' as const,
  joinedAt: '2026-04-25',
  mrrMinor: 30000,
  unitAmountMinor: 30000,
  interval: 'month' as string | null,
  subStatus: 'active' as string | null,
  isPastDue: false,
  dbMode: 'shared',
  currentPeriodEnd: null as string | null,
};

/** DB-mode → human label for the CustomerDetail "Database" field. */
const DB_MODE_LABEL: Record<string, string> = {
  shared: 'Managed (shared)',
  managed: 'Managed (dedicated)',
  self_hosted: 'Self-hosted',
  legacy: 'Legacy (shared)',
};
function dbModeLabel(mode?: string): string {
  return (mode && DB_MODE_LABEL[mode]) ?? 'Managed (shared)';
}

/** Status → badge tone + label. Covers BOTH the business account status enum and the
 *  subscription status enum (active/trial/past_due/expired/canceled) — the CustomerDetail
 *  badges show the subscription status, which can differ from the account status. */
const STATUS_BADGE: Record<string, { tone: 'success' | 'error' | 'warning' | 'info' | 'neutral'; label: string }> = {
  active: { tone: 'success', label: 'Active' },
  trial: { tone: 'info', label: 'Trial' },
  past_due: { tone: 'error', label: 'Past due' },
  pending_payment: { tone: 'warning', label: 'Pending payment' },
  provisioning: { tone: 'info', label: 'Provisioning' },
  suspended: { tone: 'warning', label: 'Suspended' },
  expired: { tone: 'warning', label: 'Expired' },
  canceled: { tone: 'neutral', label: 'Canceled' },
  deleted: { tone: 'neutral', label: 'Deleted' },
};
function statusBadge(status: string) {
  return STATUS_BADGE[status] ?? { tone: 'neutral' as const, label: status };
}

/** Billing interval → the price-line suffix ("Basic · ৳3,000 / year"). Falls back to
 *  'month' for an unknown/missing interval so the label is never blank. */
const INTERVAL_LABEL: Record<string, string> = {
  month: 'month',
  quarter: 'quarter',
  year: 'year',
  week: 'week',
  one_time: 'one-time',
};
function intervalLabel(interval?: string | null): string {
  return (interval && INTERVAL_LABEL[interval]) ?? 'month';
}

/** The subscription's billing status drives the CustomerDetail status badges — NOT the
 *  business account status. Falls back to the account status when there's no subscription. */
function subStatusOf(customer: Customer): string {
  return customer.subStatus || customer.status;
}

/** Reseller-side customer detail with header card and 5 tabs. */
type Customer = ResellerCustomer | typeof FALLBACK_CUSTOMER;

export function CustomerDetail() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { data: resellerCustomers = [] } = useResellerCustomers();
  const qc = useQueryClient();
  const [payOpen, setPayOpen] = useState(false);
  // NO `?? resellerCustomers[0]` fallback. On an unmatched id this rendered a
  // DIFFERENT customer's page — with the "Record payment" modal bound to it, so cash
  // could be recorded against the wrong business. Fall through to the placeholder only,
  // which carries id 'fallback' and is already excluded from every write below.
  const customer: Customer =
    resellerCustomers.find((c) => c.id === id) ??
    FALLBACK_CUSTOMER;
  const notFound = !resellerCustomers.some((c) => c.id === id) && resellerCustomers.length > 0;
  const realId = customer.id !== 'fallback' ? customer.id : '';
  const { data: ledger } = useResellerCustomerLedger(realId);
  const { data: domains } = useCustomerDomains(realId);
  const { data: audit } = useCustomerAudit(realId);
  const sb = statusBadge(subStatusOf(customer));

  // Loaded, and this id is not one of yours: say so. Previously this rendered another
  // customer's page with the cash-payment modal live against them.
  if (notFound) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-16 max-w-[1200px] mx-auto text-center">
        <h1 className="text-xl font-semibold text-zinc-950 dark:text-zinc-50">Customer not found</h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
          This customer may have been removed, or the link is out of date.
        </p>
        <Link to="/reseller/customers" className="mt-6 inline-block text-sm font-medium text-brand dark:text-brand-dm hover:underline">
          ← Back to all customers
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1200px] mx-auto">
      <Link
        to="/reseller/customers"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        All customers
      </Link>

      {/* Header card */}
      <Card className="p-5 sm:p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <Avatar name={customer.name} size="xl" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                  {customer.name}
                </h1>
                <Badge tone={sb.tone}>{sb.label}</Badge>
                <span className="text-sm text-zinc-500 dark:text-zinc-400">·</span>
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{customer.plan}</span>
              </div>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                Owner <span className="font-medium text-zinc-700 dark:text-zinc-200">{customer.ownerEmail}</span>{' '}
                · joined <span className="tabular-nums">{formatDate(customer.joinedAt)}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" iconLeft={<Receipt className="w-4 h-4" />} onClick={() => setPayOpen(true)}>
              Record cash payment
            </Button>
          </div>
        </div>
      </Card>

      {/* Past-due banner — only when the customer is actually past due. Dunning is
          driven by the automatic billing cron; the reseller records a payment here. */}
      {customer.isPastDue && (
        <Banner
          tone="warning"
          title="Payment past due"
          action={
            <Button size="sm" iconLeft={<Receipt className="w-4 h-4" />} onClick={() => setPayOpen(true)}>
              Record cash payment
            </Button>
          }
          className="mb-4"
        >
          The subscription auto-renewal hasn't been paid. Auto-suspend will trigger if payment isn't received.
          The system sends reminder emails automatically until the balance clears.
        </Banner>
      )}

      <CashPaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        businessId={customer.id}
        businessName={customer.name}
        onRecorded={() => {
          void qc.invalidateQueries({ queryKey: ['reseller', 'customers'] });
          void qc.invalidateQueries({ queryKey: ['reseller', 'ledger', customer.id] });
          void qc.invalidateQueries({ queryKey: ['reseller', 'payments'] });
        }}
      />

      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview', content: <OverviewTab customer={customer} ledger={ledger} audit={audit} /> },
          { id: 'subscription', label: 'Subscription', content: <SubscriptionTab customer={customer} /> },
          { id: 'users', label: 'Users', content: <UsersTab businessId={customer.id} /> },
          { id: 'ledger', label: 'Ledger', count: ledger?.rows.length, content: <LedgerView ledger={ledger} /> },
          { id: 'domains', label: 'Domains', count: domains?.length, content: <DomainsTab domains={domains} /> },
          { id: 'audit', label: 'Audit', count: audit?.length, content: <AuditTab events={audit} /> },
        ]}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

type TimelineEntry = { id: string; kind: 'payment' | 'invoice' | 'audit' | 'join'; primary: string; date: string | null };

/** Build a real "recent timeline" from the customer's ledger (invoices+payments)
 *  and audit events. Newest first, capped. Empty when there's nothing real yet. */
function buildTimeline(ledger: BusinessLedger | undefined, audit: CustomerAuditEvent[] | undefined, joinedAt: string, plan: string): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  for (const [i, r] of (ledger?.rows ?? []).entries()) {
    if (r.kind === 'payment') {
      entries.push({ id: `pay-${r.ref}-${i}`, kind: 'payment', primary: `Payment ${moneyBdt(r.creditMinor)}${r.method ? ` (${r.method})` : ''}`, date: r.date });
    } else {
      entries.push({ id: `inv-${r.ref}-${i}`, kind: 'invoice', primary: r.description || `Invoice ${r.ref}`, date: r.date });
    }
  }
  for (const e of audit ?? []) {
    entries.push({ id: `aud-${e.id}`, kind: 'audit', primary: auditWhat(e.action), date: e.occurredAt });
  }
  entries.push({ id: 'join', kind: 'join', primary: `Joined on ${plan}`, date: joinedAt });
  return entries
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
    .slice(0, 8);
}
function moneyBdt(minor: number): string {
  return `৳${Math.round(minor / 100).toLocaleString('en-US')}`;
}
function timelineIcon(kind: TimelineEntry['kind']): React.ReactNode {
  if (kind === 'payment') return <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />;
  if (kind === 'invoice') return <Receipt className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />;
  if (kind === 'join') return <History className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />;
  return <RefreshCcw className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />;
}

function OverviewTab({ customer, ledger, audit }: { customer: Customer; ledger?: BusinessLedger; audit?: CustomerAuditEvent[] }) {
  const sb = statusBadge(subStatusOf(customer));
  const timeline = buildTimeline(ledger, audit, customer.joinedAt, customer.plan);
  return (
    <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
      <Card className="lg:col-span-2 p-5 sm:p-6">
        <CardHeader title="Status" />
        <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
          <Field label="Subscription">
            <Badge tone={sb.tone}>{sb.label}</Badge>
          </Field>
          <Field label="Price">
            <Money minor={customer.unitAmountMinor ?? customer.mrrMinor} hideCents tone="neutral" className="font-medium text-zinc-950 dark:text-zinc-50" />
            <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-1">/ {intervalLabel(customer.interval)}</span>
          </Field>
          <Field label="Plan">{customer.plan}</Field>
          <Field label="Next renewal">
            <span className="tabular-nums">{customer.currentPeriodEnd ? formatDate(customer.currentPeriodEnd) : '—'}</span>
          </Field>
          <Field label="Database">
            <span className="inline-flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />
              {dbModeLabel(customer.dbMode)}
            </span>
          </Field>
          <Field label="Owner contact">{customer.ownerEmail}</Field>
        </dl>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Recent timeline" />
        {timeline.length === 0 ? (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No activity recorded yet.</p>
        ) : (
          <ul className="space-y-3 text-sm">
            {timeline.map((t) => (
              <TimelineItem
                key={t.id}
                icon={timelineIcon(t.kind)}
                primary={t.primary}
                date={t.date ? formatDate(t.date, { compact: true }) : '—'}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function SubscriptionTab({ customer }: { customer: Customer }) {
  const sb = statusBadge(subStatusOf(customer));
  // Show the REAL per-period price + interval (e.g. "৳3,000 / year"), not the
  // monthly-normalized MRR. Fall back to MRR/month only when there's no sub row.
  const priceMinor = customer.unitAmountMinor ?? customer.mrrMinor;
  // A past-due sub's period end is already in the past, so "Renews" would misread as
  // upcoming — label it "Payment due" instead.
  const renewalLabel = customer.isPastDue ? 'Payment due' : 'Renews';
  return (
    <div className="space-y-4">
      <Card className="p-5 sm:p-6">
        <CardHeader
          title={
            <span className="flex items-center gap-2 flex-wrap">
              {customer.plan} ·{' '}
              <Money minor={priceMinor} hideCents tone="neutral" className="font-semibold" />
              <span className="text-sm font-normal text-zinc-500 dark:text-zinc-400">/ {intervalLabel(customer.interval)}</span>
            </span>
          }
          subtitle={
            <span className="tabular-nums">
              Started {formatDate(customer.joinedAt, { compact: true })}
              {customer.currentPeriodEnd ? <> · {renewalLabel} {formatDate(customer.currentPeriodEnd, { compact: true })}</> : null}
            </span>
          }
          action={<Badge tone={sb.tone}>{sb.label}</Badge>}
        />
        <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-2">
          Plan changes and cancellations are managed by the business owner from their account, or by
          platform admins. Record an off-gateway payment from the header above.
        </p>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Add-ons" subtitle="None active" />
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          Customer hasn't enabled any paid add-ons yet.
        </p>
      </Card>
    </div>
  );
}

/** Users tab — this customer's members with live POS roles + edit/remove (Feature #37 + follow-up). */
function UsersTab({ businessId }: { businessId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="px-0 py-5 sm:py-6 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 sm:px-6 pb-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Users</h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
            Owners and staff with POS access. POS roles are read live from this customer's POS.
          </p>
        </div>
        <Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={() => setOpen(true)}>
          Add user
        </Button>
      </div>
      <BusinessUsersTable businessId={businessId} />
      <AddCustomerUserModal open={open} onClose={() => setOpen(false)} businessId={businessId} />
    </Card>
  );
}

// (member table + edit/remove live in components/BusinessUsersTable — shared with the admin page)

/** Add a user to one of the reseller's customer businesses — mirrors the owner Team modal. */
function AddCustomerUserModal({
  open,
  onClose,
  businessId,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string;
}) {
  const [action, setAction] = useState<'create' | 'invite'>('create');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [posRole, setPosRole] = useState<string>('Cashier');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResellerInviteResult | ResellerCreateUserResult | null>(null);

  function close() {
    setAction('create');
    setEmail('');
    setName('');
    setUsername('');
    setPosRole('Cashier');
    setPassword('');
    setBusy(false);
    setError(null);
    setResult(null);
    onClose();
  }

  async function submit() {
    if (!email.trim() || !businessId) {
      setError('A valid email is required.');
      return;
    }
    if (action === 'create' && password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (action === 'create' && username.trim() && !/^[a-z0-9][a-z0-9._-]{2,39}$/i.test(username.trim())) {
      setError('Username must be 3–40 characters: letters, digits, dot, dash or underscore.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await createResellerCustomerUser(businessId, {
        action,
        email: email.trim(),
        name: name.trim() || undefined,
        posRole,
        password: action === 'create' ? password : undefined,
        username: action === 'create' ? username.trim() || undefined : undefined,
      });
      setResult(res);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      const message = e instanceof Error && e.message ? e.message : null;
      setError(
        status === 409
          ? (message ?? 'That person already has access to this business.')
          : status === 403
            ? 'You do not have permission to add users here.'
            : message ?? (action === 'create'
              ? 'Could not create the account.'
              : 'Could not send the invite.'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={result ? ('inviteUrl' in result ? 'Invitation sent' : 'Account created') : 'Add a user'}
      size="md"
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!email.trim() || busy} loading={busy}>
              {action === 'create' ? 'Create account' : 'Send invite'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        'inviteUrl' in result ? (
          <InviteLinkResult email={result.email} inviteUrl={result.inviteUrl} />
        ) : (
          <AccountCreatedPanel email={result.email} />
        )
      ) : (
        <div className="space-y-4">
          <CreateOrInviteToggle action={action} onChange={setAction} />
          <Input
            label="Email"
            required
            type="email"
            placeholder="owner@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Full name"
            placeholder="(optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {action === 'create' && (
            <>
              <Input
                label="Username"
                placeholder="(optional — auto-generated if empty)"
                hint="Their POS + portal login username."
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <Input
                label="Password"
                required
                type="password"
                hint="Minimum 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </>
          )}
          <PosRoleSelector businessId={businessId} value={posRole} onChange={setPosRole} />
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

/** DNS/TLS status → badge tone + label (mirrors the domains enum; falls back). */
const DNS_BADGE: Record<string, { tone: 'success' | 'warning' | 'error'; label: string }> = {
  verified: { tone: 'success', label: 'Verified' },
  pending: { tone: 'warning', label: 'Pending' },
  failed: { tone: 'error', label: 'Failed' },
};
const TLS_BADGE: Record<string, { tone: 'success' | 'warning' | 'error'; label: string }> = {
  issued: { tone: 'success', label: 'Issued' },
  pending: { tone: 'warning', label: 'Pending' },
  failed: { tone: 'error', label: 'Failed' },
};

function DomainsTab({ domains }: { domains?: CustomerDomain[] }) {
  const rows = domains ?? [];
  if (rows.length === 0) {
    return (
      <EmptyTab
        icon={<Globe className="w-5 h-5" />}
        title="No custom domains"
        body="This customer uses the default platform subdomain. Custom domains added for their business will appear here."
      />
    );
  }
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto nice-scroll">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
            <tr>
              <th className="text-left font-medium px-5 sm:px-6 py-3">Hostname</th>
              <th className="text-left font-medium px-4 py-3">DNS</th>
              <th className="text-left font-medium px-4 py-3">TLS</th>
              <th className="text-left font-medium px-5 sm:px-6 py-3">Primary</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {rows.map((d) => {
              const dns = DNS_BADGE[d.dnsStatus] ?? { tone: 'warning' as const, label: d.dnsStatus };
              const tls = TLS_BADGE[d.tlsStatus] ?? { tone: 'warning' as const, label: d.tlsStatus };
              return (
                <tr key={d.id}>
                  <td className="px-5 sm:px-6 py-4">
                    <span className="inline-flex items-center gap-2 mono text-xs text-zinc-700 dark:text-zinc-200">
                      <Globe className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />
                      {d.host}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <Badge tone={dns.tone}>{dns.label}</Badge>
                  </td>
                  <td className="px-4 py-4">
                    <Badge tone={tls.tone}>{tls.label}</Badge>
                  </td>
                  <td className="px-5 sm:px-6 py-4">
                    {d.isPrimary ? <Badge tone="info">Primary</Badge> : <span className="text-zinc-400 dark:text-zinc-500">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Audit display helpers — derive who/what/when defensively from the raw event. */
function auditWho(e: CustomerAuditEvent): string {
  const role = e.actorRole ? e.actorRole.split('_').pop() : null;
  if (e.actorName) return role ? `${e.actorName} (${role})` : e.actorName;
  if (e.actorEmail) return e.actorEmail;
  return 'system';
}
function auditWhat(action: string): string {
  const s = action.replace(/[._]/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Event';
}
function auditWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(iso, { compact: true })} · ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

const AUDIT_DIFF_FIELD_LABEL: Record<string, string> = {
  package: 'Package',
  interval: 'Interval',
  priceMinor: 'Price',
};
function humanizeAuditKey(key: string): string {
  const s = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[._]/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : key;
}
function formatAuditDiffValue(field: string, v: unknown): string {
  if (field === 'priceMinor' && typeof v === 'number') return '৳' + (v / 100).toLocaleString('en-US');
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}
function isFromToShape(v: unknown): v is { from: unknown; to: unknown } {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && 'from' in v && 'to' in v;
}
function formatMetaValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function AuditTab({ events }: { events?: CustomerAuditEvent[] }) {
  const rows = events ?? [];
  if (rows.length === 0) {
    return (
      <EmptyTab
        icon={<History className="w-5 h-5" />}
        title="No audit events yet"
        body="Account and security events for this business will appear here as they happen."
      />
    );
  }
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-700">
        {rows.map((e) => {
          const failed = e.outcome === 'failure' || e.severity === 'critical' || e.severity === 'warning';
          const diffEntries = e.diff ? Object.entries(e.diff) : [];
          const metaEntries = !e.diff && e.metadata ? Object.entries(e.metadata) : [];
          return (
            <li key={e.id} className="flex items-start gap-3 px-5 sm:px-6 py-3.5">
              <span className="w-7 h-7 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex-shrink-0">
                {failed ? <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" /> : <RefreshCcw className="w-3.5 h-3.5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-950 dark:text-zinc-50">{auditWhat(e.action)}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  <span>{auditWho(e)}</span>
                  <span className="mx-1.5">·</span>
                  <span className="tabular-nums">{auditWhen(e.occurredAt)}</span>
                </div>
                {diffEntries.length > 0 && (
                  <div className="mt-2 rounded-md border border-zinc-200 dark:border-zinc-700 overflow-hidden">
                    {diffEntries.map(([k, v], i) => (
                      <div
                        key={k}
                        className={
                          i > 0
                            ? 'grid grid-cols-[100px_1fr_auto_1fr] items-center gap-2 px-3 py-1.5 text-xs border-t border-zinc-200 dark:border-zinc-700'
                            : 'grid grid-cols-[100px_1fr_auto_1fr] items-center gap-2 px-3 py-1.5 text-xs'
                        }
                      >
                        <span className="font-medium text-zinc-600 dark:text-zinc-300">{AUDIT_DIFF_FIELD_LABEL[k] ?? humanizeAuditKey(k)}</span>
                        <span className="text-zinc-500 dark:text-zinc-400 text-right">{formatAuditDiffValue(k, v.from)}</span>
                        <span className="text-zinc-400">→</span>
                        <span className="text-zinc-900 dark:text-zinc-100 font-medium">{formatAuditDiffValue(k, v.to)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {metaEntries.length > 0 && (
                  <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400 space-y-0.5">
                    {metaEntries.map(([k, v]) => (
                      <div key={k}>
                        {humanizeAuditKey(k)}:{' '}
                        {isFromToShape(v) ? (
                          <span className="text-zinc-700 dark:text-zinc-200">{formatMetaValue(v.from)} → {formatMetaValue(v.to)}</span>
                        ) : (
                          <span className="text-zinc-700 dark:text-zinc-200">{formatMetaValue(v)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/** Shared honest empty state for the Domains / Audit tabs. */
function EmptyTab({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <Card className="p-10 text-center">
      <div className="w-11 h-11 mx-auto mb-3 grid place-items-center rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400">
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">{title}</h3>
      <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400 max-w-sm mx-auto">{body}</p>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-zinc-800 dark:text-zinc-100 inline-flex items-center gap-1.5 flex-wrap">
        {children}
      </dd>
    </div>
  );
}

function TimelineItem({
  icon,
  primary,
  date,
}: {
  icon: React.ReactNode;
  primary: string;
  date: string;
}) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-zinc-900 dark:text-zinc-100">{primary}</div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums mt-0.5">{date}</div>
      </div>
    </li>
  );
}

// suppress unused
void Mail;
